import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Layout from '../../components/layout/Layout';
import { getPaymentDashboard, getPayments, addPayment, updatePayment, deletePayment, generateMonthlyInvoices, markOverdueInvoices, syncInvoiceToQbo, syncPaymentStatusFromQbo } from '../../Redux/Actions/paymentAction';
import { getStores } from '../../Redux/Actions/storeAction';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SyncIcon from '@mui/icons-material/Sync';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem,
  FormControl, InputLabel, Box, Typography, Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import toast from 'react-hot-toast';
import './payment.css';

const STATUS_COLOR = { paid: '#22c55e', pending: '#f59e0b', overdue: '#ef4444' };
const STATUS_BG = { paid: '#f0fdf4', pending: '#fffbeb', overdue: '#fef2f2' };

function StatCard({ icon, label, value, color, bg }) {
  return (
    <div className="pay-stat-card" style={{ borderTop: `4px solid ${color}` }}>
      <div className="pay-stat-icon" style={{ background: bg, color }}>
        {icon}
      </div>
      <div>
        <p className="pay-stat-label">{label}</p>
        <p className="pay-stat-value" style={{ color }}>{value}</p>
      </div>
    </div>
  );
}

function Payment({ user }) {
  const dispatch = useDispatch();
  const { dashboard, payments, loading } = useSelector(s => s.Payment);
  const { stores } = useSelector(s => s.Store);

  const [filterStatus, setFilterStatus] = useState('all');
  const [openAdd, setOpenAdd] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({
    storeId: '', type: 'monthly', amount: 75,
    paymentMethod: 'ACH', dueDate: '', memo: '',
  });
  const [paidDialog, setPaidDialog] = useState(null);
  const [paidMemo, setPaidMemo] = useState('');
  const [editDialog, setEditDialog] = useState(null);
  const [editMemo, setEditMemo] = useState('');
  const [editFields, setEditFields] = useState({ status: 'pending', amount: '', dueDate: '', paidDate: '', memo: '' });
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleteMemo, setDeleteMemo] = useState('');

  useEffect(() => {
    dispatch(getPaymentDashboard(user._id));
    dispatch(getPayments(user._id));
    dispatch(getStores(user._id));
  }, []);

  const filtered = payments.filter(p =>
    filterStatus === 'all' ? true : p.status === filterStatus
  );

  const handleAdd = async () => {
    if (!form.storeId || !form.amount) return;
    const now = new Date();
    const billingMonth = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
    const invNum = `INV-${billingMonth}-${form.storeId.slice(-4).toUpperCase()}`;
    await dispatch(addPayment({
      store: form.storeId,
      user: user._id,
      invoiceNumber: invNum,
      type: form.type,
      amount: Number(form.amount),
      paymentMethod: form.paymentMethod,
      dueDate: form.dueDate || undefined,
      memo: form.memo,
      auditMemo: (form.memo && form.memo.trim()) || '수동 청구 등록',
      billingMonth,
      status: 'pending',
    }));
    dispatch(getPaymentDashboard(user._id));
    setOpenAdd(false);
    setForm({ storeId:'', type:'monthly', amount:75, paymentMethod:'ACH', dueDate:'', memo:'' });
  };

  const confirmMarkPaid = async () => {
    if (!paidDialog) return;
    if ((paidMemo || '').trim().length < 3) {
      toast.error('납부 처리 사유(메모)를 3자 이상 입력해 주세요.');
      return;
    }
    try {
      await dispatch(updatePayment(paidDialog._id, {
        editMemo: paidMemo.trim(),
        status: 'paid',
        paidDate: new Date(),
      }));
      toast.success('납부 완료로 반영되었습니다.');
      setPaidDialog(null);
      setPaidMemo('');
      dispatch(getPaymentDashboard(user._id));
      dispatch(getPayments(user._id));
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || '실패');
    }
  };

  const openEdit = (p) => {
    setEditDialog(p);
    setEditMemo('');
    setEditFields({
      status: p.status,
      amount: String(p.amount ?? ''),
      dueDate: p.dueDate ? String(p.dueDate).slice(0, 10) : '',
      paidDate: p.paidDate ? String(p.paidDate).slice(0, 10) : '',
      memo: p.memo || '',
    });
  };

  const saveEdit = async () => {
    if (!editDialog) return;
    if ((editMemo || '').trim().length < 3) {
      toast.error('수정 사유(메모)를 3자 이상 입력해 주세요.');
      return;
    }
    try {
      await dispatch(updatePayment(editDialog._id, {
        editMemo: editMemo.trim(),
        status: editFields.status,
        amount: Number(editFields.amount),
        dueDate: editFields.dueDate || null,
        paidDate: editFields.paidDate || null,
        memo: editFields.memo,
      }));
      toast.success('저장되었습니다.');
      setEditDialog(null);
      dispatch(getPaymentDashboard(user._id));
      dispatch(getPayments(user._id));
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || '실패');
    }
  };

  const confirmDelete = async () => {
    if (!deleteDialog) return;
    if ((deleteMemo || '').trim().length < 3) {
      toast.error('삭제 사유(메모)를 3자 이상 입력해 주세요.');
      return;
    }
    try {
      await dispatch(deletePayment(deleteDialog._id, { editMemo: deleteMemo.trim() }));
      toast.success('삭제되었습니다.');
      setDeleteDialog(null);
      setDeleteMemo('');
      dispatch(getPaymentDashboard(user._id));
      dispatch(getPayments(user._id));
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || '삭제 실패');
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    const result = await dispatch(generateMonthlyInvoices(user._id));
    await dispatch(getPaymentDashboard(user._id));
    await dispatch(getPayments(user._id));
    setGenerating(false);
    alert(`${result?.count || 0} invoices generated.`);
  };

  const fmt = (n) => `$${(n || 0).toLocaleString()}`;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }) : '-';

  const maxBar = Math.max(...(dashboard?.monthlyBreakdown || []).map(m => m.amount), 1);

  return (
    <Layout title="Payment" user={user}>
      <div className="pay-root">

        {/* Stat Cards */}
        <div className="pay-stats-row">
          <StatCard icon={<AttachMoneyIcon />} label="Received This Month"
            value={fmt(dashboard?.received)} color="#22c55e" bg="#f0fdf4" />
          <StatCard icon={<PendingActionsIcon />} label="Pending"
            value={fmt(dashboard?.pending)} color="#f59e0b" bg="#fffbeb" />
          <StatCard icon={<WarningAmberIcon />} label="Overdue"
            value={fmt(dashboard?.overdue)} color="#ef4444" bg="#fef2f2" />
          <StatCard icon={<StorefrontIcon />} label="Active Stores"
            value={dashboard?.totalActiveStores || 0} color="#551FFF" bg="rgba(85,31,255,0.08)" />
        </div>

        {/* Charts + Actions row */}
        <div className="pay-mid-row">
          {/* Monthly bar chart */}
          <div className="pay-card pay-chart-card">
            <h3 className="pay-card-title">Monthly Revenue (Last 6 Months)</h3>
            <div className="pay-bar-chart">
              {(dashboard?.monthlyBreakdown || []).map((m, i) => (
                <div key={i} className="pay-bar-col">
                  <div className="pay-bar-wrap">
                    <div
                      className="pay-bar"
                      style={{ height: `${Math.round((m.amount / maxBar) * 120)}px` }}
                      title={fmt(m.amount)}
                    />
                  </div>
                  <span className="pay-bar-label">{m.month}</span>
                  <span className="pay-bar-val">{fmt(m.amount)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick stats */}
          <div className="pay-card pay-quick-card">
            <h3 className="pay-card-title">Next Month</h3>
            <div className="pay-quick-row">
              <span>Expected Revenue</span>
              <strong style={{ color: '#551FFF' }}>{fmt(dashboard?.nextMonthExpected)}</strong>
            </div>
            <div className="pay-quick-row">
              <span>New Stores (this month)</span>
              <strong>{dashboard?.newStoresThisMonth || 0}</strong>
            </div>
            <div className="pay-quick-row">
              <span>Per Store Rate</span>
              <strong>$75 / month</strong>
            </div>
            <hr style={{ margin: '12px 0', borderColor: '#f0f0f0' }} />
            <button className="pay-btn-primary" style={{ width: '100%', marginBottom: 8 }}
              onClick={() => setOpenAdd(true)}>
              <AddIcon style={{ fontSize: 16 }} /> Add Payment
            </button>
            <button className="pay-btn-outline" style={{ width: '100%', marginBottom: 8 }}
              onClick={handleGenerate} disabled={generating}>
              <AutorenewIcon style={{ fontSize: 16 }} />
              {generating ? 'Generating...' : 'Generate Monthly Invoices'}
            </button>
            <button className="pay-btn-outline" style={{ width: '100%', marginBottom: 8 }}
              onClick={async () => {
                const r = await dispatch(markOverdueInvoices());
                alert(`${r?.count || 0} invoices marked overdue`);
                dispatch(getPaymentDashboard(user._id));
                dispatch(getPayments(user._id));
              }}>
              <WarningAmberIcon style={{ fontSize: 16 }} /> Check Overdue
            </button>
            <button className="pay-btn-outline" style={{ width: '100%' }}
              onClick={async () => {
                const r = await dispatch(syncPaymentStatusFromQbo());
                alert(r?.error ? `Error: ${r.error}` : `${r?.updated || 0} payments synced from QBO`);
                dispatch(getPayments(user._id));
                dispatch(getPaymentDashboard(user._id));
              }}>
              <SyncIcon style={{ fontSize: 16 }} /> Sync from QBO
            </button>
          </div>
        </div>

        {/* Payment list */}
        <div className="pay-card">
          <div className="pay-list-header">
            <h3 className="pay-card-title" style={{ margin: 0 }}>Payment Records</h3>
            <div className="pay-filter-tabs">
              {['all','pending','paid','overdue'].map(s => (
                <button key={s} className={`pay-filter-tab${filterStatus === s ? ' active' : ''}`}
                  onClick={() => setFilterStatus(s)}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>
              ))}
            </div>
          </div>
          <div className="pay-table-wrap">
            <table className="pay-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Store</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Due Date</th>
                  <th>Paid Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign:'center', color:'#aaa', padding:24 }}>No records</td></tr>
                ) : filtered.map(p => (
                  <tr key={p._id}>
                    <td style={{ fontFamily:'monospace', fontSize:12 }}>{p.invoiceNumber || '-'}</td>
                    <td>
                      <div style={{ fontWeight:600 }}>{p.store?.name || '-'}</div>
                      <div style={{ fontSize:11, color:'#aaa' }}>{p.store?.town}, {p.store?.state}</div>
                    </td>
                    <td><span style={{ textTransform:'capitalize' }}>{p.type}</span></td>
                    <td style={{ fontWeight:700 }}>${p.amount}</td>
                    <td>{fmtDate(p.dueDate)}</td>
                    <td>{fmtDate(p.paidDate)}</td>
                    <td>
                      <span className="pay-status-badge" style={{
                        background: STATUS_BG[p.status], color: STATUS_COLOR[p.status],
                        border: `1px solid ${STATUS_COLOR[p.status]}33`
                      }}>
                        {p.status}
                      </span>
                    </td>
                    <td style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                      {p.status !== 'paid' && (
                        <button className="pay-btn-sm-green" onClick={() => { setPaidDialog(p); setPaidMemo(''); }}>
                          <CheckCircleIcon style={{ fontSize:13 }} /> Paid
                        </button>
                      )}
                      <button type="button" className="pay-btn-outline" style={{ fontSize:11, padding:'4px 8px' }}
                        onClick={() => openEdit(p)} title="수정 (메모 필수)">
                        <EditIcon style={{ fontSize:13, verticalAlign:'middle' }} />
                      </button>
                      <button type="button" className="pay-btn-outline" style={{ fontSize:11, padding:'4px 8px', color:'#b91c1c', borderColor:'#fecaca' }}
                        onClick={() => { setDeleteDialog(p); setDeleteMemo(''); }} title="삭제 (사유 필수)">
                        <DeleteOutlineIcon style={{ fontSize:13, verticalAlign:'middle' }} />
                      </button>
                      {!p.qbInvoiceId && (
                        <button className="pay-btn-sm-blue" onClick={async () => {
                          const r = await dispatch(syncInvoiceToQbo(p._id));
                          alert(r?.error ? `Error: ${r.error}` : 'QBO synced');
                          dispatch(getPayments(user._id));
                        }} title="Send to QuickBooks">
                          <CloudUploadIcon style={{ fontSize:13 }} /> QBO
                        </button>
                      )}
                      {p.qbInvoiceId && (
                        <span style={{ fontSize:10, color:'#22c55e' }} title={`QBO ID: ${p.qbInvoiceId}`}>QBO synced</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Payment Dialog */}
      <Dialog open={!!paidDialog} onClose={() => { setPaidDialog(null); setPaidMemo(''); }} maxWidth="xs" fullWidth>
        <DialogTitle style={{ fontWeight: 700 }}>납부 완료 처리</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1, pb: 1 }}>
            감사 기록을 위해 사유를 남겨 주세요.
          </Typography>
          <TextField fullWidth multiline rows={3} label="납부 처리 메모 (필수)" value={paidMemo}
            onChange={e => setPaidMemo(e.target.value)} size="small" placeholder="예: 3/15 ACH 입금 확인" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setPaidDialog(null); setPaidMemo(''); }}>취소</Button>
          <Button variant="contained" onClick={confirmMarkPaid} sx={{ background: '#22c55e' }}>확인</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle style={{ fontWeight: 700 }}>청구 수정</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>상태</InputLabel>
              <Select value={editFields.status} label="상태"
                onChange={e => setEditFields(f => ({ ...f, status: e.target.value }))}>
                <MenuItem value="pending">pending</MenuItem>
                <MenuItem value="paid">paid</MenuItem>
                <MenuItem value="overdue">overdue</MenuItem>
                <MenuItem value="cancelled">cancelled</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" label="금액" type="number" value={editFields.amount}
              onChange={e => setEditFields(f => ({ ...f, amount: e.target.value }))} fullWidth />
            <TextField size="small" label="Due" type="date" value={editFields.dueDate} InputLabelProps={{ shrink: true }}
              onChange={e => setEditFields(f => ({ ...f, dueDate: e.target.value }))} fullWidth />
            <TextField size="small" label="Paid" type="date" value={editFields.paidDate} InputLabelProps={{ shrink: true }}
              onChange={e => setEditFields(f => ({ ...f, paidDate: e.target.value }))} fullWidth />
            <TextField size="small" label="메모" value={editFields.memo} multiline rows={2}
              onChange={e => setEditFields(f => ({ ...f, memo: e.target.value }))} fullWidth />
            <TextField size="small" label="수정 사유 (감사용, 필수)" value={editMemo} multiline rows={2} required
              onChange={e => setEditMemo(e.target.value)} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialog(null)}>취소</Button>
          <Button variant="contained" onClick={saveEdit} sx={{ background: '#551FFF' }}>저장</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteDialog} onClose={() => { setDeleteDialog(null); setDeleteMemo(''); }} maxWidth="xs" fullWidth>
        <DialogTitle style={{ fontWeight: 700 }}>청구 삭제</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            삭제 후에도 감사 로그에 기록됩니다. 사유를 입력해 주세요.
          </Typography>
          <TextField fullWidth multiline rows={3} label="삭제 사유 (필수)" value={deleteMemo}
            onChange={e => setDeleteMemo(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDeleteDialog(null); setDeleteMemo(''); }}>취소</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>삭제</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ borderBottom: '3px solid #551FFF', fontWeight: 700 }}>
          Add Payment Record
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display:'flex', flexDirection:'column', gap:2, pt:2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Store</InputLabel>
              <Select value={form.storeId} label="Store"
                onChange={e => setForm(f => ({ ...f, storeId: e.target.value }))}>
                {(stores||[]).map(s => <MenuItem key={s._id} value={s._id}>{s.name} — {s.town}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select value={form.type} label="Type"
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                <MenuItem value="deposit">Deposit</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" label="Amount ($)" type="number" value={form.amount}
              onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} fullWidth />
            <FormControl fullWidth size="small">
              <InputLabel>Payment Method</InputLabel>
              <Select value={form.paymentMethod} label="Payment Method"
                onChange={e => setForm(f => ({ ...f, paymentMethod: e.target.value }))}>
                {['cash','check','ACH','card','other'].map(m =>
                  <MenuItem key={m} value={m}>{m.toUpperCase()}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField size="small" label="Due Date" type="date" value={form.dueDate}
              onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
              InputLabelProps={{ shrink: true }} fullWidth />
            <TextField size="small" label="Memo" value={form.memo}
              onChange={e => setForm(f => ({ ...f, memo: e.target.value }))}
              multiline rows={2} fullWidth />
            <Box sx={{ display:'flex', gap:1, justifyContent:'flex-end' }}>
              <button className="pay-btn-outline" onClick={() => setOpenAdd(false)}>Cancel</button>
              <button className="pay-btn-primary" onClick={handleAdd}>Save</button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default Payment;
